'use client';
import {
  useParams,
  usePathname,
  useRouter,
  useSearchParams,
} from 'next/navigation';
import React, { FC, useEffect, useRef, useState } from 'react';
import SuggestionCard from './SuggestionCard';
import Link from 'next/link';
import { useMessageStore } from '@/helpers/top_message';
import { MagnifyingGlass, X } from '@phosphor-icons/react';
import Cookies from 'js-cookie';
import { getSearchProducts } from '@/utils/typesense/getSearchProducts';
import { COUNTRY_LOCALE_MAP, getRegionalUrl } from '@/lib/utils';

interface SearchProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  position: number;
}

interface Suggestions {
  title: string;
  handle: string;
  thumbnail: string;
  rrp: string;
}

const SearchUI: FC<SearchProps> = ({ show = true, setShow, position }) => {
  const [suggestions, setSuggestions] = useState<Suggestions[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null); //to apply debounce
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { isMessageOpen } = useMessageStore();
  const [topbarHeight, setTopbarHeight] = useState(56);
  const [scrollTop, setScrollTop] = useState(0);

  const router = useRouter();
  const params = useParams<{ countryCode: string }>();

  const locale = COUNTRY_LOCALE_MAP[params.countryCode || 'au'];

  const fetchSuggestions = async (query: string) => {
    try {
      if (query.length > 0) {
        const suggestions = await getSearchProducts(query, locale);
        setSuggestions(suggestions);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestions?.length === 1) {
      setShow(false);
      router.push(
        getRegionalUrl(
          `/product/${suggestions[0]?.handle}`,
          params?.countryCode
        )
      );
      return;
    }
    const cleanedQuery = query.trim();
    if (cleanedQuery === '') return;
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    if (inputRef.current) inputRef.current.blur();

    setShow(false);
    router.push(
      getRegionalUrl(
        `/search?query=${encodeURIComponent(cleanedQuery)}`,
        params?.countryCode
      )
    );
  };

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
    if (!show) {
      setQuery('');
    }
  }, [show]);

  useEffect(() => {
    if (!query) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setShowSuggestions(true);

    const delayDebounceFn = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  useEffect(() => {
    const updateTopbarHeight = () => {
      const topbarElement = document.getElementById('topbar');
      if (topbarElement) {
        setTopbarHeight(topbarElement.clientHeight);
      } else {
        setTopbarHeight(0);
      }
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.removedNodes.length || mutation.addedNodes.length) {
          updateTopbarHeight();
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    updateTopbarHeight();

    return () => observer.disconnect();
  }, [isMessageOpen, topbarHeight]);

  useEffect(() => {
    const handleScroll = () => {
      const currentPosition = window.scrollY;
      setScrollTop(currentPosition);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrollTop]);

  useEffect(() => {
    setShow(false);
    const current = pathname.startsWith('/product');
    if (!current) {
      Cookies.remove('prevCategory');
    }
  }, [pathname, searchParams]);

  return (
    <div
      style={{ top: 64 }}
      onClick={() => {
        setShow(false);
      }}
      className={`w-full ${show ? 'translate-y-0' : '-translate-y-16 opacity-0 pointer-events-none'} transition-all duration-300 absolute z-10 h-screen flex  flex-col`}
    >
      <div
        id="searchbar"
        onClick={(e) => e.stopPropagation()}
        className={`w-full transition-all px-4 duration-300 z-20 border-off-white border-y-2 py-3 flex items-center justify-center bg-white`}
      >
        <form
          onSubmit={(e) => {
            handleSearch(e);
          }}
          className="max-w-[1412px] w-full justify-center bg-off-white rounded-xl flex items-center"
        >
          <MagnifyingGlass
            size={20}
            className="mx-4"
            color="#666D80"
            weight="bold"
          />
          <input
            ref={inputRef}
            className="w-full focus:outline-none font-medium text-[16px] font-proxima_font placeholder:text-black/50 focus:bg-gray-100 h-12 pr-3 bg-off-white rounded-r-full"
            type="search"
            placeholder="Search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
            }}
          />

          <X
            onClick={() => {
              setShow(false);
            }}
            size={20}
            weight="bold"
            className="mr-4 cursor-pointer"
          />
        </form>
      </div>
      <div
        className={`w-full h-full overflow-auto bg-black relative ${show ? 'opacity-100 transition-all duration-500' : 'opacity-0'}  bg-opacity-50`}
      >
        <div
          style={{
            height:
              topbarHeight && scrollTop <= topbarHeight
                ? `calc(100dvh - ${137 + topbarHeight - scrollTop}px)`
                : `calc(100dvh - 137px)`,
          }}
          onClick={() => {
            setShow(false);
          }}
          className="overflow-auto"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full pb-4 ${showSuggestions ? 'translate-y-0' : '-translate-y-full'} bg-white transition-all duration-300`}
          >
            <div className="max-w-desktop mx-auto overflow-auto no-scrollbar flex md:flex-row  flex-col md:px-8 px-4 h-full md:h-auto">
              <div className="md:w-[30%] w-full py-4 flex flex-col gap-4">
                {!!suggestions?.length && (
                  <h1 className="text-[16px] font-proxima_font text-[#818898] font-semibold">
                    Top Suggestions
                  </h1>
                )}
                <ul className="text-base font-proxima_font flex flex-col gap-[16px] font-medium">
                  {suggestions &&
                    suggestions?.slice(0, 6).map((item, index) => {
                      return (
                        <Link
                          key={index}
                          href={getRegionalUrl(
                            `/product/${item?.handle}`,
                            params?.countryCode
                          )}
                          onClick={() => setShow(false)}
                        >
                          <li
                            className="cursor-pointer text-[14px] font-proxima_font font-semibold hover:text-black/70 text-black"
                            key={index}
                          >
                            {item?.title}
                          </li>
                        </Link>
                      );
                    })}
                </ul>
              </div>
              <div className="md:w-[70%] w-full py-4 flex flex-col md:h-full h-full">
                <div className="flex md:hidden justify-between py-3">
                  <p className="text-[16px] font-semibold font-proxima_font text-[#818898]">
                    <span className="mr-1">{suggestions?.length}</span>
                    Result{suggestions?.length > 1 && 's'}
                  </p>
                  <Link
                    prefetch={false}
                    href={getRegionalUrl(
                      `/search?query=${encodeURIComponent(query.trim())}`,
                      params?.countryCode
                    )}
                    onClick={() => setShow(false)}
                    className="text-[12px] cursor-pointer font-semibold "
                  >
                    View {suggestions?.length > 1 ? 'all results' : 'result'}{' '}
                    for &quot;{query}&quot;
                  </Link>
                </div>
                <div className="w-full md:h-full h-auto grid md:grid-cols-3 grid-cols-2 md:grid-rows-1 gap-[10px]">
                  {suggestions &&
                    suggestions?.slice(0, 3).map((item, index2) => {
                      return (
                        <SuggestionCard
                          key={index2}
                          item={item}
                          layout={1}
                          onClick={() => setShow(false)}
                        />
                      );
                    })}
                </div>

                <div className="pt-5">
                  <Link
                    prefetch={false}
                    href={getRegionalUrl(
                      `/search?query=${encodeURIComponent(query.trim())}`,
                      params?.countryCode
                    )}
                    onClick={() => setShow(false)}
                    className="text-center w-full px-4 py-3 rounded-xl hidden md:block bg-black text-white hover:opacity-80 cursor-pointer transition-all duration-300 border-2 border-black font-semibold font-proxima_font text-[16px]"
                  >
                    View {suggestions?.length > 1 ? 'all results' : 'result'}{' '}
                    for &quot;{query}&quot;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchUI;
